import { BsFillFileEarmarkPptFill } from 'react-icons/bs';
import { FaFileCsv, FaFileExcel, FaFileLines, FaFilePdf, FaFileWord } from 'react-icons/fa6';

import { GOOGLE_LIVE_PREVIEW, OFFFICE_LIVE_PREVIEW } from './constant';
import { DOCUMENT_TYPE } from './enum';

/**
 * Remove querystring from file URL
 */
export const removeQueryString = (url: string) => {
  return url?.split('?')[0].split('#')[0];
};

/**
 * Get file extension from file URL
 */
export const getFileExtension = (url: string) => {
  return removeQueryString(url)?.split(/[#?]/)?.[0]?.split('.')?.pop()?.trim();
};

/**
 * Get file extension from file name
 */
export const getFileExtensionFromFileName = (name: string) => {
  const items = name?.split('.');
  if (!items || items?.length === 0) return '';
  return '.' + items[items?.length - 1];
};

/**
 * Get office live preview link from file URL
 */
export const getOfficeLivePreviewURL = (url: string = '') => {
  return `${OFFFICE_LIVE_PREVIEW}${encodeURIComponent(url)}`;
};

/**
 * Get office live preview link from file URL
 */
export const getGoogleLivePreviewURL = (url: string = '') => {
  return `${GOOGLE_LIVE_PREVIEW}${encodeURIComponent(url)}&embedded=true`;
};

export const encodeURIFileName = (fileName: string) => {
  return fileName ? encodeURIComponent(fileName) : '';
};

/**
 * Add blob SAS token to file URL
 */
export const addTokenURL = (url: string, token: string | null) => {
  if (token) {
    return `${url}?${token}`;
  }
  return url;
};

/**
 * Format size of KB
 */
export const formatBytes = (bytes: number, decimals = 2) => {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export function formatBytestoKBytes(bytes: number, decimals = 2) {
  if (!+bytes) return '0';
  const dm = decimals < 0 ? 0 : decimals;

  return `${parseFloat((bytes / 1024).toFixed(dm))}`;
}

export const isOfficeSupportedType = (fileType: string) => {
  return [
    DOCUMENT_TYPE.DOC,
    DOCUMENT_TYPE.DOCX,
    DOCUMENT_TYPE.XLS,
    DOCUMENT_TYPE.XLSX,
    DOCUMENT_TYPE.PPT,
    DOCUMENT_TYPE.PPTX,
  ].includes(fileType as DOCUMENT_TYPE);
};

export const isSeperatedValueFormat = (fileType: string) => {
  return [DOCUMENT_TYPE.CSV, DOCUMENT_TYPE.TSV].includes(fileType as DOCUMENT_TYPE);
};

export const isPDFFormat = (fileType: string) => {
  return fileType === DOCUMENT_TYPE.PDF;
};

export const isMarkdownFormat = (fileType: string) => {
  return fileType === DOCUMENT_TYPE.MARKDOWN;
};

export const isTxtFormat = (fileType: string) => {
  return fileType === DOCUMENT_TYPE.TXT;
};

export const renderFileIcon = (fileName: string) => {
  const fileIconsStyle = {
    pdf: {
      icon: FaFilePdf,
      color: 'orange.500',
    },
    doc: {
      icon: FaFileWord,
      color: 'blue.500',
    },
    docx: {
      icon: FaFileWord,
      color: 'blue.500',
    },
    xlsx: {
      icon: FaFileExcel,
      color: 'green.500',
    },
    csv: {
      icon: FaFileCsv,
      color: 'green.500',
    },
    pptx: {
      icon: BsFillFileEarmarkPptFill,
      color: 'red.500',
    },
    default: {
      icon: FaFileLines,
      color: 'black.500',
    },
  };
  const fileType = getFileExtension(fileName) as keyof typeof fileIconsStyle;
  return fileIconsStyle[fileType] ?? fileIconsStyle['default'];
};
